import React from 'react';
import { Alert, Collapse, IconButton, Box } from '@mui/material';
import { CloudOff, Close, Refresh } from '@mui/icons-material';
import { useOnlineStatus } from '@hooks/useOnlineStatus';
import { useHasChanged } from '@hooks/useHasChanged';

interface OfflineIndicatorProps {
  showBanner?: boolean;
  position?: 'top' | 'bottom';
}

export const OfflineIndicator: React.FC<OfflineIndicatorProps> = ({
  showBanner = true,
  position = 'top',
}) => {
  const { isOnline, isServerReachable, checkConnection } = useOnlineStatus({
    showNotifications: !showBanner,
  });
  const [dismissed, setDismissed] = React.useState(false);

  const showIndicator = !isOnline || !isServerReachable;

  if (useHasChanged(showIndicator) && !showIndicator) {
    setDismissed(false);
  }

  if (!showBanner || !showIndicator || dismissed) {
    return null;
  }

  const message = !isOnline
    ? 'No internet connection'
    : 'Unable to reach server';

  const severity = !isOnline ? 'error' : 'warning';

  return (
    <Box
      sx={{
        position: 'fixed',
        [position]: 0,
        left: 0,
        right: 0,
        zIndex: (theme) => theme.zIndex.snackbar + 1,
      }}
    >
      <Collapse in={showIndicator && !dismissed}>
        <Alert
          severity={severity}
          icon={<CloudOff />}
          action={
            <>
              <IconButton
                size="small"
                color="inherit"
                onClick={() => checkConnection()}
                title="Check connection"
              >
                <Refresh />
              </IconButton>
              <IconButton
                size="small"
                color="inherit"
                onClick={() => setDismissed(true)}
                title="Dismiss"
              >
                <Close />
              </IconButton>
            </>
          }
          sx={{
            borderRadius: 0,
            '& .MuiAlert-message': {
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            },
          }}
        >
          {message}
        </Alert>
      </Collapse>
    </Box>
  );
};
